/*
 * Koco Loco password experience.
 *
 * Step 2: the machine's password field. It opens while the visitor types and shakes when refused.
 * Step 3: the field is Shopify's real storefront password form. The script sends what the visitor typed with
 * fetch and reads Shopify's answer. The password is checked by Shopify on its servers and appears nowhere in
 * the theme.
 *
 * Step 4: the sign. Each knock moves it one step: a little snow, more snow, then the rest slides off and its word
 * shows. Progress is kept in localStorage (when the browser allows it) so a reload keeps what was found.
 *
 * Step 5: the hammer. Tapping it makes it hop out of the snow and fly into a small badge in the corner; from then
 * on the visitor is "holding" it (progress.hammer = 1). It is used on the radio in the next step.
 *
 * Step 6: the radio. Tapped without the hammer it only buzzes. With the hammer in hand, the hammer flies from its
 * badge, swings, and the radio breaks open, showing "IT". The hammer is used up (progress.hammer = 2).
 *
 * Step 7: the rock. Tapping it lifts it into a big close-up showing what is under it ("EASY?"); tapping again puts
 * it back where it was. It can be lifted as often as the visitor likes; the first time counts as finding the clue.
 *
 * Step 9: when Shopify accepts the password, a full-screen ACCESS GRANTED plays (the supplied pixel-art words), then
 * the machine's screen reads PULL AGAIN.
 * Step 10: the lever is pulled, the three reels spin and stop on V, I, P; the screen welcomes the visitor and an
 * ENTER THE WORLD button appears.
 *
 * Local testing: on the Shopify CLI dev server (127.0.0.1) only, ?kpwdev=1 swaps the Shopify check for a local
 * stand-in from dev/ (never uploaded to a theme). On the real store domain this switch does nothing.
 */
(() => {
  const root = document.querySelector('.kpw');
  if (!root || root.dataset.kpwReady) return;
  root.dataset.kpwReady = 'true';

  const stage = root.querySelector('.kpw__stage');
  const form = root.querySelector('.kpw__form');
  const field = root.querySelector('[data-kpw-field]');
  const input = root.querySelector('[data-kpw-input]');
  if (!stage || !form || !field || !input) return;

  /* ---- the two compositions: portrait (phones) and landscape (computers, phones turned sideways) ----
   * All coordinates are scene units of the picture that is shown, so the same code drives both. */
  const PORTRAIT = {
    w: 1024,
    h: 1536,
    text: 21, // height of the painted placeholder text
    k: 1, // size of the objects relative to the portrait scene
    px: 1, // spread of the particle clouds
    hit: { x: 23.3, y: 75.5 }, // where the hammer lands on the radio, in % of the stage
    rock: { x: 62.5, y: 93.2 },
    sparks: { x: 51, y: 53 },
    hammerSnow: { x: 77, y: 85.5, spreadX: 10, spreadY: 2 },
    signFlakes: { x: 3, y: 44, spreadX: 20, spreadY: 6 },
    swing: { ready: [320, 1050], back: [334, 1034], blow: [293, 1084], fallen: [262, 1222] },
    reels: [
      { x: 355, w: 106, letterW: 86.3 },
      { x: 466, w: 108, letterW: 40.4 },
      { x: 580, w: 112, letterW: 66.2 },
    ],
    win: { y: 724, h: 186 },
    cell: 130,
    letterH: 86,
    dateH: 64,
  };
  const LANDSCAPE = {
    w: 1672,
    h: 941,
    text: 18,
    k: 0.83,
    px: 0.6,
    hit: { x: 29.1, y: 74.8 },
    rock: { x: 58, y: 92 },
    sparks: { x: 50.3, y: 55 },
    hammerSnow: { x: 66, y: 93.5, spreadX: 6, spreadY: 1.5 },
    signFlakes: { x: 10, y: 42.5, spreadX: 13, spreadY: 5 },
    swing: { ready: [566, 597], back: [580, 582], blow: [540, 630], fallen: [510, 764] },
    reels: [
      { x: 700, w: 87, letterW: 69.2 },
      { x: 793, w: 92, letterW: 32.4 },
      { x: 890, w: 92, letterW: 53.1 },
    ],
    win: { y: 445, h: 148 },
    cell: 104,
    letterH: 69,
    dateH: 51,
  };
  const landscapeQuery = window.matchMedia('(orientation: landscape)');
  const lay = () => (landscapeQuery.matches ? LANDSCAPE : PORTRAIT);

  /* ---- field size: built with a 16px font and shrunk to match the artwork's ~21 scene-unit text ---- */
  const BUILD_FONT = 16; // px; never below 16 or iOS zooms on focus

  const fit = () => {
    const width = stage.getBoundingClientRect().width;
    if (!width) return;
    root.style.setProperty('--kpw-s', ((lay().text * width) / lay().w / BUILD_FONT).toFixed(4));
  };
  fit();
  landscapeQuery.addEventListener('change', fit);
  if ('ResizeObserver' in window) new ResizeObserver(fit).observe(stage);
  else window.addEventListener('resize', fit);

  const sync = () => field.classList.toggle('is-open', document.activeElement === input || input.value !== '');
  input.addEventListener('focus', sync);
  input.addEventListener('input', sync);
  input.addEventListener('blur', sync);

  // Tapping anywhere else closes an empty field (the keyboard goes away on touch screens)
  document.addEventListener('pointerdown', (event) => {
    if (!field.contains(event.target) && document.activeElement === input) input.blur();
  });


  /* ---- progress, kept between visits when the browser lets us ---- */
  const STORE_KEY = 'kocoPw:v1';
  const progress = { sign: 0, hammer: 0, radio: 0, rock: 0 }; // hammer: 0 in the snow, 1 held, 2 used; radio: 0 intact, 1 broken
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORE_KEY) || '{}');
    if (Number.isInteger(saved.sign) && saved.sign >= 0 && saved.sign <= 3) progress.sign = saved.sign;
    if (Number.isInteger(saved.hammer) && saved.hammer >= 0 && saved.hammer <= 2) progress.hammer = saved.hammer;
    if (saved.radio === 0 || saved.radio === 1) progress.radio = saved.radio;
    if (saved.rock === 0 || saved.rock === 1) progress.rock = saved.rock;
  } catch (error) {
    /* storage blocked: the experience just starts fresh each visit */
  }
  const save = () => {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(progress));
    } catch (error) {
      /* ignore */
    }
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // When the script moves the focus (to the lever, to the way in), no ring is drawn around the invisible button; the
  // ring comes back as soon as the visitor uses the keyboard (Tab and the like).
  const quietFocus = (element) => {
    if (!element) return;
    root.classList.add('is-quiet-focus');
    element.focus({ preventScroll: true });
    document.addEventListener('keydown', () => root.classList.remove('is-quiet-focus'), { once: true });
  };
  const signButton = root.querySelector('[data-kpw-sign]');
  const flakes = root.querySelector('[data-kpw-flakes]');

  /* ---- the sign: three knocks ---- */
  const SIGN_LABELS = [
    'Old wooden sign, buried in snow',
    'Old wooden sign, buried in snow',
    'Old wooden sign, half cleared of snow',
    'Old wooden sign: WAS',
  ];
  const showSign = (level, { instant = false } = {}) => {
    root.dataset.sign = String(level);
    root.classList.toggle('is-settled', level === 3 && instant);
    if (signButton) {
      signButton.setAttribute('aria-label', SIGN_LABELS[level]);
      signButton.setAttribute('aria-disabled', level === 3 ? 'true' : 'false');
    }
  };

  // Clumps of snow that drop and fall. Origin and spread are in % of the stage; size and fall in cqw.
  const dropSnow = (count, { x: ox = lay().signFlakes.x, y: oy = lay().signFlakes.y, spreadX = lay().signFlakes.spreadX, spreadY = lay().signFlakes.spreadY, fall: fallBase = 3.5, size: sizeBase = 0.9, kind = '' } = {}) => {
    if (!flakes || reducedMotion.matches) return;
    for (let i = 0; i < count; i++) {
      const flake = document.createElement('i');
      flake.className = kind ? `kpw__flake is-${kind}` : 'kpw__flake';
      const size = sizeBase + Math.random() * sizeBase * 2; // cqw
      const x = ox + Math.random() * spreadX;
      const y = oy + Math.random() * spreadY;
      const fall = fallBase + Math.random() * 6; // cqw
      flake.style.cssText = [
        `left:${x}%`,
        `top:${y}%`,
        `--size:calc(${size} * var(--cq))`,
        `--dx:calc(${(Math.random() - 0.5) * 3} * var(--cq))`,
        `--dy:calc(${fall * 6} * var(--cq))`,
        `--rot:${(Math.random() - 0.5) * 120}deg`,
        `--dur:${0.7 + Math.random() * 0.6}s`,
      ].join(';');
      flake.addEventListener('animationend', () => flake.remove());
      flakes.appendChild(flake);
    }
  };

  // Bits thrown from a point and pulled down by gravity. x, y: % of the stage; speeds in cqw per second.
  const launch = (count, { x, y, kind, speed = [10, 24], angle = [-165, -15], gravity = 70, life = [650, 1100], size = [0.6, 1.4], grow = 1 }) => {
    if (!flakes || reducedMotion.matches) return;
    const between = ([low, high]) => low + Math.random() * (high - low);
    for (let i = 0; i < count; i++) {
      const bit = document.createElement('i');
      bit.className = `kpw__flake is-${kind}`;
      bit.style.cssText = `left:${x}%;top:${y}%;--size:calc(${between(size)} * var(--cq));animation:none`;
      flakes.appendChild(bit);
      const theta = (between(angle) * Math.PI) / 180;
      const v = between(speed);
      const vx = Math.cos(theta) * v;
      const vy = Math.sin(theta) * v;
      const ms = between(life);
      const spin = (Math.random() - 0.5) * 900;
      const steps = 10;
      const frames = [];
      for (let k = 0; k <= steps; k++) {
        const t = (k / steps) * (ms / 1000);
        const f = k / steps;
        frames.push({
          transform: `translate(calc(${(vx * t).toFixed(2)} * var(--cq)), calc(${(vy * t + 0.5 * gravity * t * t).toFixed(2)} * var(--cq))) rotate(${(spin * f).toFixed(0)}deg) scale(${(1 + (grow - 1) * f).toFixed(2)})`,
          opacity: f < 0.7 ? 1 : 1 - (f - 0.7) / 0.3,
          offset: f,
        });
      }
      bit.animate(frames, { duration: ms, easing: 'linear', fill: 'forwards' }).onfinish = () => bit.remove();
    }
  };

  let knockLock = 0;
  const signPictures = [null, null, root.querySelector('.kpw__sign--medium'), root.querySelector('.kpw__sign--clear')];
  // the next picture of the sign may still be on its way: wait for it a moment rather than show an empty sign
  const pictureReady = (img) => {
    forceLoad(img);
    return !img || img.complete
      ? Promise.resolve()
      : new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          window.setTimeout(resolve, 900);
        });
  };
  const knock = async () => {
    if (progress.sign >= 3) return;
    if (progress.sign + 1 >= 2) await pictureReady(signPictures[progress.sign + 1]);
    if (progress.sign >= 3) return;
    const now = Date.now();
    if (now < knockLock) return; // one knock at a time
    knockLock = now + 450;

    progress.sign += 1;
    save();
    showSign(progress.sign);

    root.style.setProperty('--kpw-amp', `${[0, 0.8, 1.5, 2.2][progress.sign]}deg`);
    dropSnow([0, 5, 12, 22][progress.sign]);
    if (!reducedMotion.matches) {
      root.classList.remove('is-sign-shaking');
      void root.offsetWidth; // restart the animation
      root.classList.add('is-sign-shaking');
      window.setTimeout(() => root.classList.remove('is-sign-shaking'), 760);
    }
    if (progress.sign === 3) {
      window.setTimeout(() => root.classList.add('is-settled'), 800);
      root.dispatchEvent(new CustomEvent('kpw:clue', { bubbles: true, detail: { id: 'was' } }));
    }
  };

  if (progress.sign >= 2) forceLoad(signPictures[progress.sign]);
  showSign(progress.sign, { instant: true });
  if (signButton) signButton.addEventListener('click', knock);


  /* ---- the hammer: tap it and it is yours ---- */
  const hammerImage = root.querySelector('[data-kpw-hammer]');
  const hammerHit = root.querySelector('[data-kpw-hammer-hit]');
  const held = root.querySelector('[data-kpw-held]');

  // With the hammer in hand the pointer becomes a hammer over the radio; the site's hand cursor steps aside there
  function setHammerCursor(on) {
    root.classList.toggle('has-hammer', on);
    const radioButton = root.querySelector('[data-kpw-radio]');
    if (radioButton) {
      if (on) radioButton.setAttribute('data-no-cursor', '');
      else radioButton.removeAttribute('data-no-cursor');
    }
  }

  const showHeld = (isNew) => {
    if (!held) return;
    held.hidden = false;
    setHammerCursor(true); // on computers the pointer turns into a hammer over the radio
    if (isNew) {
      held.classList.add('is-new');
      window.setTimeout(() => held.classList.remove('is-new'), 500);
    }
  };

  const putAwayHammer = () => {
    if (hammerImage) hammerImage.hidden = true;
    if (hammerHit) hammerHit.hidden = true;
  };

  let hammerFlying = false;
  const pickUpHammer = () => {
    if (progress.hammer !== 0) return;
    progress.hammer = 1;
    save();
    root.dispatchEvent(new CustomEvent('kpw:hammer', { bubbles: true }));

    if (!hammerImage || !held || reducedMotion.matches) {
      putAwayHammer();
      showHeld(false);
      return;
    }

    // Fly from where it lies to where the badge will be: hop up, then travel (the badge pops in on arrival)
    if (hammerHit) hammerHit.hidden = true;
    held.hidden = false;
    held.style.visibility = 'hidden';
    const from = hammerImage.getBoundingClientRect();
    const to = held.getBoundingClientRect();
    held.hidden = true;
    held.style.visibility = '';
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const scale = (to.width * 0.78) / from.width;

    dropSnow(7, { ...lay().hammerSnow, fall: 1.2, size: 0.6 });

    hammerFlying = true;
    const flight = hammerImage.animate(
      [
        { transform: 'translate(0, 0) rotate(32deg) scale(1)', offset: 0 },
        { transform: 'translate(0, calc(-4 * var(--cq))) rotate(22deg) scale(1.18)', offset: 0.28, easing: 'cubic-bezier(0.5, 0, 0.9, 0.6)' },
        { transform: `translate(${dx}px, ${dy}px) rotate(-28deg) scale(${scale})`, offset: 1 },
      ],
      { duration: 700, easing: 'ease-out', fill: 'forwards' }
    );
    flight.onfinish = () => {
      hammerFlying = false;
      putAwayHammer();
      flight.cancel();
      showHeld(true);
    };
  };

  if (progress.hammer >= 1) putAwayHammer();
  if (progress.hammer === 1) showHeld(false);
  if (hammerHit) hammerHit.addEventListener('click', pickUpHammer);


  /* ---- the radio: buzz without the hammer, break with it ---- */
  const radioHit = root.querySelector('[data-kpw-radio]');
  let radioBusy = false;

  const showRadio = (level) => {
    root.dataset.radio = String(level);
    if (radioHit) {
      radioHit.setAttribute('aria-disabled', level === 1 ? 'true' : 'false');
      radioHit.setAttribute('aria-label', level === 1 ? 'Broken radio, with "IT" written inside' : 'Old radio');
    }
  };

  const pulseClass = (name, ms) => {
    root.classList.remove(name);
    void root.offsetWidth; // restart the animation
    root.classList.add(name);
    window.setTimeout(() => root.classList.remove(name), ms);
  };

  const buzzRadio = () => {
    announce('The old radio crackles, then goes quiet.');
    if (reducedMotion.matches) return;
    pulseClass('is-buzzing', 650);
  };

  // Where the hammer lands on the radio, in % of the stage

  const impactFlash = () => {
    if (!flakes || reducedMotion.matches) return;
    for (const name of ['kpw__impact', 'kpw__ring']) {
      const el = document.createElement('i');
      el.className = name;
      el.style.cssText = `left:${lay().hit.x}%;top:${lay().hit.y}%`;
      el.addEventListener('animationend', () => el.remove());
      flakes.appendChild(el);
    }
  };

  const breakRadio = () => {
    forceLoad(root.querySelector('.kpw__radio--broken'));
    progress.radio = 1;
    progress.hammer = 2;
    save();
    showRadio(1);
    if (held) held.hidden = true;
    setHammerCursor(false);
    if (!reducedMotion.matches) {
      pulseClass('is-bursting', 600);
      pulseClass('is-hit', 420);
      // the radio bursts: pieces and sparks fly out and fall back, with a puff of snow and dust
      launch(12, { x: lay().hit.x - 4 * lay().px, y: lay().hit.y - 1, kind: 'debris', speed: [14, 30], angle: [-160, -20], gravity: 85, life: [800, 1250], size: [0.7, 1.7] });
      launch(14, { x: lay().hit.x - 4 * lay().px, y: lay().hit.y - 1, kind: 'spark', speed: [18, 38], angle: [-175, -5], gravity: 60, life: [350, 650], size: [0.25, 0.5] });
      launch(10, { x: lay().hit.x - 6 * lay().px, y: lay().hit.y, kind: 'snow', speed: [4, 12], angle: [-150, -30], gravity: 22, life: [900, 1400], size: [0.6, 1.5] });
      launch(5, { x: lay().hit.x - 6 * lay().px, y: lay().hit.y + 1, kind: 'dust', speed: [2, 6], angle: [-150, -30], gravity: -4, life: [900, 1300], size: [3, 5], grow: 2.2 });
    }
    root.dispatchEvent(new CustomEvent('kpw:clue', { bubbles: true, detail: { id: 'it' } }));
  };

  // The strike: the radio takes the blow (a squash and a flash), a beat of hit-stop, then it bursts open
  const strikeRadio = () => {
    if (reducedMotion.matches) {
      breakRadio();
      return;
    }
    impactFlash();
    pulseClass('is-struck', 110);
    window.setTimeout(breakRadio, 100);
  };

  // The hammer leaves its badge in an arc, rises over the radio, pulls back, swings over the top about its
  // handle and strikes; then it drops away, used up. All positions are the grip (the handle's end).
  const swingHammer = () => {
    const toolSource = held && held.querySelector('img');
    if (!toolSource || reducedMotion.matches) {
      breakRadio();
      return;
    }
    const stageBox = stage.getBoundingClientRect();
    const u = stageBox.width / lay().w; // px per scene unit
    const k = lay().k; // objects are a little smaller in the landscape scene
    const size = 120 * k * u;
    const GRIP = { x: 0.12, y: 0.88 }; // where the handle ends, as a fraction of the picture
    const at = (x, y) => ({ x: stageBox.left + x * u, y: stageBox.top + y * u });

    // Start with the hammer exactly as the badge shows it (centred, turned -28deg), grip placed to match
    const badge = held.getBoundingClientRect();
    const startScale = (badge.width * 0.78) / size;
    const rad = (-28 * Math.PI) / 180;
    const ox = (GRIP.x - 0.5) * size * startScale;
    const oy = (GRIP.y - 0.5) * size * startScale;
    const start = {
      x: badge.left + badge.width / 2 + ox * Math.cos(rad) - oy * Math.sin(rad),
      y: badge.top + badge.height / 2 + ox * Math.sin(rad) + oy * Math.cos(rad),
    };

    const swing = lay().swing;
    const ready = at(...swing.ready); // grip held up and to the right of the radio
    const back = at(...swing.back); // pulled back before the swing
    const blow = at(...swing.blow); // grip at the moment of impact (the head lands on the radio)
    const fallen = at(...swing.fallen);
    const arc = { x: (start.x + ready.x) / 2 + 14 * k * u, y: Math.min(start.y, ready.y) - 95 * k * u };

    const tool = document.createElement('img');
    tool.className = 'kpw__tool';
    tool.src = toolSource.src;
    tool.alt = '';
    tool.style.width = `${size}px`;
    tool.style.left = `${start.x - GRIP.x * size}px`;
    tool.style.top = `${start.y - GRIP.y * size}px`;
    tool.style.transformOrigin = `${GRIP.x * 100}% ${GRIP.y * 100}%`;
    document.body.appendChild(tool);
    held.hidden = true;
    setHammerCursor(false);

    const pose = (point, turn, grow) => `translate(${(point.x - start.x).toFixed(1)}px, ${(point.y - start.y).toFixed(1)}px) rotate(${turn}deg) scale(${grow})`;
    const duration = 1750;
    const IMPACT = 0.6;
    const animation = tool.animate(
      [
        { transform: pose(start, -28, startScale), opacity: 1, offset: 0 },
        { transform: pose(arc, -52, 1.12), opacity: 1, offset: 0.2, easing: 'ease-in-out' }, // up and over
        { transform: pose(ready, -10, 1.18), opacity: 1, offset: 0.36, easing: 'ease-out' }, // arrives, held up
        { transform: pose(back, 18, 1.2), opacity: 1, offset: 0.46, easing: 'cubic-bezier(0.7, 0, 1, 0.55)' }, // pulls back
        { transform: pose(blow, -186, 1.14), opacity: 1, offset: IMPACT }, // the swing over the top
        { transform: pose(blow, -186, 1.14), opacity: 1, offset: IMPACT + 0.05, easing: 'ease-out' }, // hit-stop
        { transform: pose({ x: blow.x + 6 * u, y: blow.y - 8 * u }, -168, 1.14), opacity: 1, offset: 0.76, easing: 'ease-in' }, // bounces back
        { transform: pose(fallen, -120, 1.05), opacity: 0, offset: 1 }, // drops to the ground, used up
      ],
      { duration, fill: 'forwards' }
    );
    window.setTimeout(strikeRadio, duration * IMPACT);
    animation.onfinish = () => {
      tool.remove();
      radioBusy = false;
    };
  };

  const useRadio = () => {
    if (radioBusy || hammerFlying || progress.radio === 1) return;
    if (progress.hammer === 1) {
      radioBusy = true;
      swingHammer();
    } else {
      buzzRadio();
    }
  };

  if (progress.radio === 1) forceLoad(root.querySelector('.kpw__radio--broken'));
  showRadio(progress.radio);
  if (radioHit) radioHit.addEventListener('click', useRadio);


  /* ---- the rock: lift it into a close-up, tap again to put it back ---- */
  const rockHit = root.querySelector('[data-kpw-rock]');
  const rockImage = root.querySelector('.kpw__rock');
  const liftedSrc = root.dataset.liftedSrc;
  const [liftedW, liftedH] = (root.dataset.liftedSize || '913x804').split('x').map(Number);
  let zoom = null; // { button, image, from }
  let rockBusy = false;

  const dustAtRock = (heavy) => {
    launch(heavy ? 7 : 4, { x: lay().rock.x, y: lay().rock.y, kind: 'dust', speed: [2, 7], angle: [-170, -10], gravity: -3, life: [800, 1200], size: [3, 5], grow: 2.2 });
    launch(heavy ? 12 : 6, { x: lay().rock.x, y: lay().rock.y, kind: 'snow', speed: [5, 14], angle: [-160, -20], gravity: 40, life: [700, 1100], size: [0.5, 1.2] });
  };

  const closeRock = () => {
    if (!zoom || rockBusy) return;
    rockBusy = true;
    const { button, image, from } = zoom;
    button.classList.remove('is-in');
    const finish = () => {
      button.remove();
      zoom = null;
      rockBusy = false;
      document.removeEventListener('keydown', onRockKey);
      if (rockHit) rockHit.focus({ preventScroll: true });
    };
    if (reducedMotion.matches) {
      root.classList.remove('is-rock-open');
      finish();
      return;
    }
    const back = image.animate(
      [
        { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
        { transform: from, opacity: 1, offset: 0.8 },
        { transform: from, opacity: 0, offset: 1 },
      ],
      { duration: 480, easing: 'cubic-bezier(0.5, 0, 0.8, 0.4)', fill: 'forwards' }
    );
    // the resting rock reappears just as the lifted one lands on it, with a small thud
    window.setTimeout(() => {
      root.classList.remove('is-rock-open');
      pulseClass('is-hit', 420);
      dustAtRock(false);
    }, 480 * 0.78);
    back.onfinish = finish;
  };

  function onRockKey(event) {
    if (event.key === 'Escape') closeRock();
  }

  const openRock = () => {
    if (!liftedSrc || !rockImage || rockBusy || zoom) return;
    rockBusy = true;

    const maxWidth = Math.min(window.innerWidth * 0.96, window.innerHeight * 0.86 * (liftedW / liftedH), 780);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'kpw__zoom';
    button.setAttribute('aria-label', 'Under the rock, ants spell: EASY? Tap to put the rock back.');
    const image = document.createElement('img');
    image.className = 'kpw__zoom-image';
    image.src = liftedSrc;
    image.alt = '';
    image.width = liftedW;
    image.height = liftedH;
    image.style.width = `${maxWidth}px`;
    image.style.height = `${(maxWidth * liftedH) / liftedW}px`;
    button.appendChild(image);
    document.body.appendChild(button);

    // From where the rock rests (same width) to the middle of the screen, much bigger
    const rest = rockImage.getBoundingClientRect();
    const end = image.getBoundingClientRect();
    const dx = rest.left + rest.width / 2 - (end.left + end.width / 2);
    const dy = rest.top + rest.height / 2 - (end.top + end.height / 2);
    const from = `translate(${dx}px, ${dy}px) scale(${rest.width / end.width}) rotate(0deg)`;
    zoom = { button, image, from };

    root.classList.add('is-rock-open');
    document.addEventListener('keydown', onRockKey);
    button.addEventListener('click', closeRock);
    button.focus({ preventScroll: true });

    if (progress.rock === 0) {
      progress.rock = 1;
      save();
      root.dispatchEvent(new CustomEvent('kpw:clue', { bubbles: true, detail: { id: 'easy' } }));
    }

    if (reducedMotion.matches) {
      button.classList.add('is-in');
      rockBusy = false;
      return;
    }
    dustAtRock(true);
    window.requestAnimationFrame(() => button.classList.add('is-in'));
    image.animate(
      [
        { transform: from, opacity: 0, offset: 0 },
        { transform: 'translate(0, -1.5%) scale(1.03) rotate(-1.5deg)', opacity: 1, offset: 0.72 },
        { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1, offset: 1 },
      ],
      { duration: 600, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' }
    ).onfinish = () => {
      rockBusy = false;
    };
  };

  if (rockHit) rockHit.addEventListener('click', openRock);


  /* ---- the lever: the date for everyone, V I P once the password is found ---- */
  const lever = root.querySelector('[data-kpw-lever]');
  const reelsHost = root.querySelector('[data-kpw-reels]');
  const screenLine = root.querySelector('[data-kpw-screen]');
  const screenLabel = root.querySelector('[data-kpw-screen-text]');
  const enterLink = root.querySelector('[data-kpw-enter]');
  let pulling = false;
  let resetTimer = 0;
  let spinToken = 0; // bumped whenever the machine is reset, so a spin that is still running cannot finish into a newer state

  // Where the three reel windows are (scene units) and what shows in them at rest: the Koco faces
  const FACE_SELECTORS = ['.kpw__head--1', '.kpw__head--2', '.kpw__head--3'];
  const LETTERS = ['v', 'i', 'p'];
  const reelList = () => lay().reels.map((reel, index) => ({ ...reel, face: FACE_SELECTORS[index], letter: LETTERS[index] }));

  // The opening date, one part per reel ("15/01/27" -> 15, 01, 27)
  const dateParts = (() => {
    const parts = (root.dataset.launchDate || '15/01/27').split(/[^0-9]+/).filter(Boolean).slice(0, 3);
    while (parts.length < 3) parts.push('--');
    return parts;
  })();

  const place = (el, w, h, x, y) => {
    el.style.cssText += `;width:calc(var(--u) * ${w});height:calc(var(--u) * ${h});left:calc(var(--u) * ${x});top:calc(var(--u) * ${y})`;
    return el;
  };
  const sym = (src, w, h, x, y) => {
    const img = document.createElement('img');
    img.className = 'kpw__sym';
    img.src = src;
    img.alt = '';
    img.decoding = 'async';
    return place(img, w, h, x, y);
  };
  const digits = (text, w, h, x, y) => {
    const el = document.createElement('span');
    el.className = 'kpw__sym kpw__digits';
    el.textContent = text;
    el.style.fontSize = `calc(var(--u) * ${h * 0.86})`;
    return place(el, w, h, x, y);
  };

  const setScreen = (text, small) => {
    if (!screenLine || !screenLabel) return;
    screenLabel.textContent = text;
    screenLine.classList.toggle('is-welcome', !!small);
    screenLine.hidden = false;
    screenLine.style.animation = 'none';
    void screenLine.offsetWidth;
    screenLine.style.animation = '';
  };

  const stopTheFaces = (hide) => {
    root.querySelectorAll('.kpw__head').forEach((face) => {
      face.style.visibility = hide ? 'hidden' : '';
    });
  };

  // Back to the resting machine: the Koco faces, the painted message
  const resetMachine = (instant) => {
    window.clearTimeout(resetTimer);
    spinToken += 1;
    const done = () => {
      if (reelsHost) {
        reelsHost.textContent = '';
        reelsHost.hidden = true;
        reelsHost.classList.remove('is-fading');
      }
      stopTheFaces(false);
      root.classList.remove('is-date', 'is-spinning');
      if (!root.classList.contains('is-pull-again') && screenLine) screenLine.hidden = true;
      pulling = false;
    };
    if (instant || reducedMotion.matches || !reelsHost || reelsHost.hidden) {
      done();
      return;
    }
    reelsHost.classList.add('is-fading');
    window.setTimeout(done, 420);
  };

  // The lever pulled by anyone: the reels stop on the opening date, nothing is unlocked
  const revealDate = () => {
    root.classList.add('is-date');
    pulling = false;
    root.dispatchEvent(new CustomEvent('kpw:date', { bubbles: true }));
    screenLine?.querySelector('.kpw__screen-arrow')?.setAttribute('hidden', '');
    setScreen('Opening date', true);
    if (!reducedMotion.matches) {
      launch(10, { x: lay().sparks.x, y: lay().sparks.y, kind: 'spark', speed: [8, 22], angle: [-170, -10], gravity: 40, life: [600, 1100], size: [0.3, 0.6] });
    }
    resetTimer = window.setTimeout(() => resetMachine(false), 8000);
  };

  // The password was found: V I P, a welcome, and the way in
  const revealVip = () => {
    window.clearTimeout(resetTimer);
    root.classList.add('is-vip');
    root.dispatchEvent(new CustomEvent('kpw:vip', { bubbles: true }));
    if (screenLine) {
      screenLine.querySelector('.kpw__screen-arrow')?.setAttribute('hidden', '');
      setScreen('Welcome to the inner circle', true);
    }
    if (!reducedMotion.matches) {
      launch(18, { x: lay().sparks.x, y: lay().sparks.y, kind: 'spark', speed: [10, 30], angle: [-175, -5], gravity: 40, life: [700, 1300], size: [0.3, 0.7] });
      pulseClass('is-hit', 420);
    }
    if (enterLink) {
      enterLink.hidden = false;
      if (!reducedMotion.matches) enterLink.classList.add('is-in');
      quietFocus(enterLink);
    }
  };

  /**
   * Builds the three reels and spins them. finals(index) says what each one lands on; done runs once the last one stops.
   * With reduced motion the reels just show their final symbols.
   */
  const spinReels = (finals, done) => {
    if (!reelsHost) return;
    reelsHost.textContent = '';
    reelsHost.hidden = false;
    const faces = reelList().map((r) => root.querySelector(r.face));
    const faceSrc = faces.map((f) => f && (f.currentSrc || f.src));
    const faceBox = faces.map((f) => (f ? { w: f.naturalWidth || f.width, h: f.naturalHeight || f.height } : { w: 100, h: 98 }));
    const centerY = lay().win.h / 2;
    const stageBox = stage.getBoundingClientRect();
    const unit = stageBox.width / lay().w;
    let finished = 0;

    reelList().forEach((reel, index) => {
      const holder = document.createElement('div');
      holder.className = 'kpw__reel';
      holder.style.cssText = `left:calc(var(--u) * ${reel.x});top:calc(var(--u) * ${lay().win.y});width:calc(var(--u) * ${reel.w});height:calc(var(--u) * ${lay().win.h})`;
      const strip = document.createElement('div');
      strip.className = 'kpw__strip';
      holder.appendChild(strip);
      const final = finals(index);
      const finalNode = (cellsDown) => {
        const height = final.h || lay().letterH;
        const y = centerY + cellsDown * lay().cell - height / 2;
        return final.kind === 'digits'
          ? digits(final.text, reel.w - 4, lay().letterH, 2, centerY + cellsDown * lay().cell - lay().letterH / 2)
          : sym(final.src, final.w, height, (reel.w - final.w) / 2, y);
      };

      if (reducedMotion.matches) {
        strip.appendChild(finalNode(0));
        reelsHost.appendChild(holder);
        finished += 1;
        if (finished === lay().reels.length) done();
        return;
      }

      // item 0 is the face that is already there (same place), then faces tumbling past, then the final symbol
      const first = faces[index];
      const faceBoxRect = first ? first.getBoundingClientRect() : null;
      const faceLeft = faceBoxRect ? (faceBoxRect.left - stageBox.left) / unit - reel.x : (reel.w - faceBox[index].w) / 2;
      const faceTop = faceBoxRect ? (faceBoxRect.top - stageBox.top) / unit - lay().win.y : centerY - faceBox[index].h / 2;
      strip.appendChild(sym(faceSrc[index], faceBox[index].w, faceBox[index].h, faceLeft, faceTop));
      const count = 15 + index * 5;
      for (let k = 1; k <= count; k++) {
        const pick = (index + k * 2 + (k % 3)) % faces.length;
        strip.appendChild(
          sym(faceSrc[pick], faceBox[pick].w, faceBox[pick].h, (reel.w - faceBox[pick].w) / 2, centerY + k * lay().cell - faceBox[pick].h / 2)
        );
      }
      const L = count + 1;
      strip.appendChild(finalNode(L));
      reelsHost.appendChild(holder);

      const y = (cells) => `translateY(calc(var(--u) * ${(-cells * lay().cell).toFixed(1)}))`;
      const duration = 2100 + index * 700;
      const run = strip.animate(
        [
          { transform: y(0), filter: 'blur(0px)', offset: 0, easing: 'cubic-bezier(0.5, 0, 1, 1)' },
          { transform: y(1.6), filter: 'blur(1.4px)', offset: 0.08, easing: 'linear' },
          { transform: y(L - 1.5), filter: 'blur(1.4px)', offset: 0.8, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' },
          { transform: y(L + 0.14), filter: 'blur(0.4px)', offset: 0.93, easing: 'ease-in-out' },
          { transform: y(L), filter: 'blur(0px)', offset: 1 },
        ],
        { duration, fill: 'forwards' }
      );
      run.onfinish = () => {
        finished += 1;
        holder.classList.add('is-stopped');
        if (!reducedMotion.matches) pulseClass('is-hit', 300);
        if (finished === lay().reels.length) window.setTimeout(done, 350);
      };
    });
    if (reducedMotion.matches) {
      if (finished === lay().reels.length) return;
    }
    root.classList.add('is-spinning');
  };

  const letterFinal = (index) => ({
    kind: 'letter',
    src: root.dataset[`letter${LETTERS[index].toUpperCase()}`],
    w: lay().reels[index].letterW,
  });
  // Marquee pictures for the parts of 15/01/27 (width / height of each picture); any other part falls back to text
  const DATE_ART = { 15: 1.292, '01': 1.285, 27: 1.358 };
  const dateFinal = (index) => {
    const part = dateParts[index];
    const src = root.getAttribute(`data-date-${part}`); // (dataset would mangle a dash before a digit)
    if (src && DATE_ART[part]) return { kind: 'letter', src, w: lay().dateH * DATE_ART[part], h: lay().dateH };
    return { kind: 'digits', text: part };
  };


  /* ---- the quiet "push the lever" reminder: shown after a few still seconds, gone once the lever is used ---- */
  const showHintSoon = () => {
    window.clearTimeout(hintTimer);
    hintTimer = window.setTimeout(() => {
      if (!pulling && !root.classList.contains('is-vip') && !root.classList.contains('is-rock-open')) root.classList.add('is-hint');
    }, 6000);
  };
  let hintTimer = 0;
  const hideHint = () => {
    window.clearTimeout(hintTimer);
    root.classList.remove('is-hint');
  };
  showHintSoon();

  const pullLever = () => {
    const unlocked = root.classList.contains('is-pull-again');
    if (root.classList.contains('is-vip')) return; // the way in is already open
    if (pulling) return;
    hideHint();
    // pulling again while the date is still showing: start from the resting machine
    if (root.classList.contains('is-date') || (reelsHost && !reelsHost.hidden)) resetMachine(true);
    pulling = true;
    root.classList.add('is-pulling');
    if (navigator.vibrate) navigator.vibrate(25);

    forceLoad(root.querySelector('.kpw__lever--down'));
    if (!reducedMotion.matches) {
      root.classList.add('is-lever-down');
      window.setTimeout(() => root.classList.remove('is-lever-down'), 650);
      pulseClass('is-hit', 420);
    }
    if (screenLine && !unlocked) screenLine.hidden = true;
    if (screenLine && unlocked) screenLine.hidden = true;
    stopTheFaces(true);
    const token = ++spinToken;
    spinReels(unlocked ? letterFinal : dateFinal, () => {
      if (token !== spinToken) return; // the machine was reset meanwhile (for instance the password was accepted during the spin)
      root.classList.remove('is-pulling');
      if (unlocked) revealVip();
      else revealDate();
    });
    if (reducedMotion.matches) stopTheFaces(true);
  };

  root.addEventListener('kpw:pull-again', () => {
    showHintSoon();
    resetMachine(true);
    root.classList.add('is-lever-hint');
    if (screenLine) {
      // the screen says PULL AGAIN (it may have been showing the date before)
      if (screenLabel) screenLabel.textContent = 'PULL AGAIN';
      screenLine.classList.remove('is-welcome');
      screenLine.querySelector('.kpw__screen-arrow')?.removeAttribute('hidden');
      screenLine.hidden = false;
      screenLine.classList.add('is-action');
      screenLine.addEventListener('click', pullLever);
    }
  });
  if (lever) lever.addEventListener('click', pullLever);


  /* ---- pictures for later moments: fetched when the browser is idle, or at the first touch ---- */
  // a picture needed right now (a state restored from an earlier visit, a lever about to move) is fetched at once
  function forceLoad(img) {
    if (img && !img.getAttribute('src') && img.dataset.deferSrc) img.src = img.dataset.deferSrc;
  }
  const deferredImages = [...root.querySelectorAll('img[data-defer-src]')];
  const warmUrls = [
    liftedSrc,
    root.dataset.letterV,
    root.dataset.letterI,
    root.dataset.letterP,
    root.getAttribute('data-date-15'),
    root.getAttribute('data-date-01'),
    root.getAttribute('data-date-27'),
    root.dataset.grantedAccess,
    root.dataset.grantedGranted,
  ];
  let warmed = false;
  const warmUp = () => {
    if (warmed) return;
    warmed = true;
    deferredImages.forEach((img) => {
      img.src = img.dataset.deferSrc;
    });
    warmUrls.filter(Boolean).forEach((url) => {
      const picture = new Image();
      picture.decoding = 'async';
      picture.src = url;
    });
  };
  const whenIdle = (fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 3500 }) : window.setTimeout(fn, 1500));
  if (document.readyState === 'complete') whenIdle(warmUp);
  else window.addEventListener('load', () => whenIdle(warmUp), { once: true });
  root.addEventListener('pointerdown', warmUp, { once: true, passive: true });

  /* ---- what a screen reader hears (the picture has no text) ---- */
  const liveRegion = document.querySelector('[data-kpw-live]');
  const announce = (text) => {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = text;
    }, 40);
  };
  const CLUE_SPEECH = {
    was: 'The sign now reads: WAS.',
    it: 'Inside the broken radio, a label reads: IT.',
    easy: 'Under the rock, ants spell out: EASY?',
  };
  root.addEventListener('kpw:clue', (event) => announce(CLUE_SPEECH[event.detail && event.detail.id] || ''));
  root.addEventListener('kpw:hammer', () => announce('You picked up a hammer.'));
  root.addEventListener('kpw:date', () => announce(`The reels stop on the opening date: ${(root.dataset.launchDate || '').replace(/\//g, ' ')}.`));
  root.addEventListener('kpw:pull-again', () => announce('Access granted. The machine says: pull again. Use the lever.'));
  root.addEventListener('kpw:vip', () => announce('The reels read V, I, P. Welcome to the inner circle. The button Enter the world is now available.'));

  /* ---- local stand-in for Shopify (dev server only) ---- */
  const isLocalDevServer = ['127.0.0.1', 'localhost'].includes(location.hostname);
  let devCheck = null;
  const devReady =
    isLocalDevServer && new URLSearchParams(location.search).has('kpwdev')
      ? new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'http://127.0.0.1:9393/koco-password-devstub.js';
          script.onload = () => {
            devCheck = window.KOCO_PW_DEV_CHECK || null;
            resolve();
          };
          script.onerror = () => resolve();
          document.head.appendChild(script);
        })
      : Promise.resolve();

  /* ---- what the visitor typed, as the password Shopify expects to be offered ---- */
  const normalise = (raw) => raw.normalize('NFKC').replace(/\s+/g, ' ').trim().toUpperCase();
  // The password is upper case and ends with a question mark: offer the typed phrase, and with the mark added
  // when it is missing. At most two requests per attempt.
  const attemptsFor = (raw) => {
    const typed = normalise(raw);
    return typed === '' ? [] : typed.endsWith('?') ? [typed] : [typed, `${typed}?`];
  };

  /**
   * Asks Shopify. Resolves to 'granted', 'refused', or 'unknown' (something unexpected: the caller then lets
   * the browser submit the form normally, so a challenge page or an error still works).
   */
  async function askShopify(candidate) {
    const body = new URLSearchParams(new FormData(form));
    body.set('password', candidate);
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        redirect: 'follow',
        signal: controller.signal,
      });
      const path = new URL(response.url, location.href).pathname;
      if (path.startsWith('/challenge')) return 'unknown';
      const html = await response.text();
      if (html.includes('kpw__stage')) return 'refused'; // the password page again, with its error
      // A good password makes Shopify set its cookie and send the visitor to the storefront
      if (response.ok && path !== '/password') return 'granted';
      return 'unknown';
    } catch (error) {
      console.warn('[koco-password] request failed', error);
      return 'unknown';
    } finally {
      window.clearTimeout(timer);
    }
  }

  async function verify(raw) {
    await devReady;
    const attempts = attemptsFor(raw);
    if (devCheck) {
      for (const candidate of attempts) if (await devCheck(candidate)) return 'granted';
      return 'refused';
    }
    for (const candidate of attempts) {
      const result = await askShopify(candidate);
      if (result !== 'refused') return result === 'granted' ? 'granted' : { unknown: candidate };
    }
    return 'refused';
  }

  /* ---- states ---- */
  let busy = false;
  let nopeTimer = 0;

  const refuse = () => {
    announce('That is not it.');
    field.classList.remove('is-nope');
    void field.offsetWidth; // restart the animation
    field.classList.add('is-nope');
    // a timer rather than animationend: with reduced motion there is no animation to end
    window.clearTimeout(nopeTimer);
    nopeTimer = window.setTimeout(() => field.classList.remove('is-nope'), 420);
  };

  const grant = () => {
    root.classList.add('is-granted');
    input.blur();
    input.value = '';
    input.disabled = true;
    field.classList.remove('is-open');
    root.dispatchEvent(new CustomEvent('kpw:granted', { bubbles: true }));
    showAccessGranted();
  };

  // Back to the machine after ACCESS GRANTED: its screen now says PULL AGAIN (the lever comes next)
  const enterPullAgain = () => {
    root.classList.add('is-pull-again');
    const screenText = root.querySelector('[data-kpw-screen]');
    if (screenText) screenText.hidden = false;
    quietFocus(lever);
    root.dispatchEvent(new CustomEvent('kpw:pull-again', { bubbles: true }));
  };

  // The full-screen moment: lines open, the letters crash in one by one, a flash, a hold, then back to the machine
  function showAccessGranted() {
    if (document.querySelector('.kpw__granted')) return;
    const screen = document.createElement('div');
    screen.className = 'kpw__granted';
    screen.setAttribute('role', 'status');
    screen.setAttribute('aria-label', 'Access granted');

    screen.innerHTML =
      '<div class="kpw__granted-rays"></div>' +
      '<div class="kpw__granted-line kpw__granted-line--top"></div>' +
      '<div class="kpw__granted-words" aria-hidden="true">' +
      `<img class="kpw__granted-word kpw__granted-word--1" src="${root.dataset.grantedAccess}" width="365" height="84" alt="">` +
      `<img class="kpw__granted-word kpw__granted-word--2" src="${root.dataset.grantedGranted}" width="440" height="84" alt="">` +
      '</div>' +
      '<div class="kpw__granted-line kpw__granted-line--bottom"></div>' +
      '<div class="kpw__granted-flash"></div>';
    document.body.appendChild(screen);

    let done = false;
    const leave = () => {
      if (done) return;
      done = true;
      screen.classList.add('is-leaving');
      window.setTimeout(() => {
        screen.remove();
        enterPullAgain();
      }, reducedMotion.matches ? 0 : 520);
    };
    const hold = reducedMotion.matches ? 1800 : 3200;
    const timer = window.setTimeout(leave, hold);
    // a tap skips the wait once the title has landed
    window.setTimeout(() => {
      screen.addEventListener('click', () => {
        window.clearTimeout(timer);
        leave();
      });
    }, reducedMotion.matches ? 300 : 1500);
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    if (input.value.trim() === '') {
      input.focus();
      return;
    }
    busy = true;
    field.classList.add('is-busy');
    form.setAttribute('aria-busy', 'true');
    const result = await verify(input.value);
    busy = false;
    field.classList.remove('is-busy');
    form.removeAttribute('aria-busy');

    if (result === 'granted') {
      grant();
    } else if (result === 'refused') {
      refuse();
    } else {
      // Unexpected answer (a challenge page, a network error): let the browser submit the form for real
      input.value = result.unknown;
      HTMLFormElement.prototype.submit.call(form);
    }
  });
})();
